import type { SortDescriptor } from "react-stately";

import { memo, useCallback, useEffect, useState } from "react";

import type { DesignerFilterType } from "../../DesignerDropdownFilter";

import {
  designerCompletedTableConfig,
  designerDueTodayTableConfig,
  designerOverdueTableConfig,
} from "../../../config/tables/designer";
import {
  designerAssignedToAnyAwaitingReplyTableConfig,
  designerAssignedToAnyBeingRevisedTableConfig,
  designerAssignedToAnyInProgressTableConfig,
  designerAssignedToAnyOthersTableConfig,
  designerAssignedToAnyReadyForReviewTableConfig,
} from "../../../config/tables/designer/assignedToAnySections";
import {
  useDesignerRequestCounts,
  useDesignerRequestData,
  useDesignerSectionStates,
} from "../../../hooks/designer";
import { useResetFilterOnEmptySearch } from "../../../hooks/shared";
import { SectionTitleBadge } from "../../SectionTitleBadge";
import { LazyConfigurableTable } from "../../tables/LazyConfigurableTable";

interface AssignedToAnyTabProps {
  onFilterChange?: (filter: DesignerFilterType) => void;
  searchValue?: string;
  selectedFilter?: DesignerFilterType;
}

type SectionType =
  | "awaiting-reply"
  | "being-revised"
  | "in-progress"
  | "others"
  | "ready-for-review";

const SECTIONS: { id: SectionType }[] = [
  { id: "in-progress" },
  { id: "being-revised" },
  { id: "awaiting-reply" },
  { id: "ready-for-review" },
  { id: "others" },
];

// Default sort configurations for single-table views
const SINGLE_TABLE_SORT_DEFAULTS: Record<
  "completed" | "due-today" | "overdue",
  { column: string; direction: "ascending" | "descending" }
> = {
  completed: {
    column: "completedDate",
    direction: "descending",
  },
  "due-today": {
    column: "createdAt",
    direction: "descending",
  },
  overdue: {
    column: "dueDate",
    direction: "ascending",
  },
};

// Section-specific table configurations
const getSectionConfig = (sectionId: SectionType) => {
  switch (sectionId) {
    case "awaiting-reply": {
      return designerAssignedToAnyAwaitingReplyTableConfig;
    }
    case "being-revised": {
      return designerAssignedToAnyBeingRevisedTableConfig;
    }
    case "in-progress": {
      return designerAssignedToAnyInProgressTableConfig;
    }
    case "others": {
      return designerAssignedToAnyOthersTableConfig;
    }
    case "ready-for-review": {
      return designerAssignedToAnyReadyForReviewTableConfig;
    }
    default: {
      return designerAssignedToAnyInProgressTableConfig;
    }
  }
};

function AssignedToAnyTabComponent({
  onFilterChange,
  searchValue = "",
  selectedFilter = "open",
}: AssignedToAnyTabProps) {
  // Fetch counts for smart loading optimization
  const { data: countsData, isLoading: isCountsLoading } =
    useDesignerRequestCounts();

  // Determine if we should use single-table mode
  const isSingleTableMode =
    selectedFilter === "completed" ||
    selectedFilter === "due-today" ||
    selectedFilter === "overdue";

  // Manage independent state for each section (each has its own default sort)
  const { createSectionHandlers, sectionStates } = useDesignerSectionStates();

  // Manage state for single-table view
  const [singleTableState, setSingleTableState] = useState<{
    currentPage: number;
    itemsPerPage: number;
    sortDescriptor: SortDescriptor;
  }>({
    currentPage: 1,
    itemsPerPage: 10,
    sortDescriptor:
      SINGLE_TABLE_SORT_DEFAULTS[
        selectedFilter as "completed" | "due-today" | "overdue"
      ] || SINGLE_TABLE_SORT_DEFAULTS.completed,
  });

  const [cachedTotalPages, setCachedTotalPages] = useState<null | number>(null);
  const [cachedTotalItems, setCachedTotalItems] = useState<null | number>(null);

  // Single table handlers
  const handleSingleTablePageChange = useCallback((page: number) => {
    setSingleTableState((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  const handleSingleTableItemsPerPageChange = useCallback(
    (itemsPerPage: number) => {
      setSingleTableState((prev) => ({
        ...prev,
        currentPage: 1,
        itemsPerPage,
      }));
    },
    [],
  );

  const handleSingleTableSortChange = useCallback(
    (sortDescriptor: SortDescriptor) => {
      setSingleTableState((prev) => ({
        ...prev,
        sortDescriptor,
      }));
    },
    [],
  );

  // Fetch single-table data if in single-table mode
  const singleTableData = useDesignerRequestData({
    direction:
      singleTableState.sortDescriptor.direction === "ascending"
        ? "asc"
        : "desc",
    enabled: isSingleTableMode,
    filter: selectedFilter,
    limit: singleTableState.itemsPerPage,
    page: singleTableState.currentPage,
    search: searchValue,
    skipCount: false,
    sortBy: singleTableState.sortDescriptor.column as string,
    tab: "assigned-to-any",
    viewMode: "single-table",
  });

  useEffect(() => {
    if (singleTableState.currentPage === 1) {
      if (singleTableData.data?.pagination?.totalPages) {
        setCachedTotalPages(singleTableData.data.pagination.totalPages);
      }
      if (singleTableData.data?.total != null) {
        setCachedTotalItems(singleTableData.data.total);
      }
    }
  }, [
    singleTableState.currentPage,
    singleTableData.data?.pagination?.totalPages,
    singleTableData.data?.total,
  ]);

  useEffect(() => {
    setSingleTableState((prev) => ({ ...prev, currentPage: 1 }));
    setCachedTotalPages(null);
    setCachedTotalItems(null);
  }, [selectedFilter, searchValue]);

  // Fetch data for all sections at the top level (React Hooks rules)
  // Only fetch section data if NOT in single-table mode
  const inProgressState = sectionStates["in-progress"];
  const beingRevisedState = sectionStates["being-revised"];
  const awaitingReplyState = sectionStates["awaiting-reply"];
  const readyForReviewState = sectionStates["ready-for-review"];
  const othersState = sectionStates.others;

  const inProgressData = useDesignerRequestData({
    direction:
      inProgressState.sortDescriptor.direction === "ascending" ? "asc" : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: inProgressState.itemsPerPage,
    page: inProgressState.currentPage,
    search: searchValue,
    section: "in-progress",
    skipCount: false,
    sortBy: inProgressState.sortDescriptor.column as string,
    tab: "assigned-to-any",
    viewMode: "section",
  });

  const beingRevisedData = useDesignerRequestData({
    direction:
      beingRevisedState.sortDescriptor.direction === "ascending"
        ? "asc"
        : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: beingRevisedState.itemsPerPage,
    page: beingRevisedState.currentPage,
    search: searchValue,
    section: "being-revised",
    skipCount: false,
    sortBy: beingRevisedState.sortDescriptor.column as string,
    tab: "assigned-to-any",
    viewMode: "section",
  });

  const awaitingReplyData = useDesignerRequestData({
    direction:
      awaitingReplyState.sortDescriptor.direction === "ascending"
        ? "asc"
        : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: awaitingReplyState.itemsPerPage,
    page: awaitingReplyState.currentPage,
    search: searchValue,
    section: "awaiting-reply",
    skipCount: false,
    sortBy: awaitingReplyState.sortDescriptor.column as string,
    tab: "assigned-to-any",
    viewMode: "section",
  });

  const readyForReviewData = useDesignerRequestData({
    direction:
      readyForReviewState.sortDescriptor.direction === "ascending"
        ? "asc"
        : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: readyForReviewState.itemsPerPage,
    page: readyForReviewState.currentPage,
    search: searchValue,
    section: "ready-for-review",
    skipCount: false,
    sortBy: readyForReviewState.sortDescriptor.column as string,
    tab: "assigned-to-any",
    viewMode: "section",
  });

  const othersData = useDesignerRequestData({
    direction:
      othersState.sortDescriptor.direction === "ascending" ? "asc" : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: othersState.itemsPerPage,
    page: othersState.currentPage,
    search: searchValue,
    section: "others",
    skipCount: false,
    sortBy: othersState.sortDescriptor.column as string,
    tab: "assigned-to-any",
    viewMode: "section",
  });

  const sectionQueries = [
    inProgressData,
    beingRevisedData,
    awaitingReplyData,
    readyForReviewData,
    othersData,
  ];

  useResetFilterOnEmptySearch({
    isAlreadyBroadest: selectedFilter === "all",
    isReady: isSingleTableMode
      ? singleTableData.isSuccess && !singleTableData.isFetching
      : sectionQueries.every((q) => q.isSuccess && !q.isFetching),
    onSwitchToAll: () => onFilterChange?.("all"),
    searchTerm: searchValue,
    totalMatches: isSingleTableMode
      ? (singleTableData.data?.total ?? 0)
      : sectionQueries.reduce((sum, q) => sum + (q.data?.total ?? 0), 0),
  });

  // Get appropriate table config based on filter
  const getSingleTableConfig = () => {
    switch (selectedFilter) {
      case "completed": {
        return designerCompletedTableConfig;
      }
      case "due-today": {
        return designerDueTodayTableConfig;
      }
      case "overdue": {
        return designerOverdueTableConfig;
      }
      default: {
        return designerAssignedToAnyInProgressTableConfig;
      }
    }
  };

  // Render single-table view
  if (isSingleTableMode) {
    const { data, isLoading } = singleTableData;
    const tableConfig = getSingleTableConfig();

    return (
      <div className="space-y-6">
        <LazyConfigurableTable
          config={tableConfig}
          data={data?.requests || []}
          isLoading={isLoading}
          paginationProps={{
            metadata: {
              currentPage: singleTableState.currentPage,
              itemsPerPage: singleTableState.itemsPerPage,
              totalItems: data?.total ?? cachedTotalItems,
              totalPages:
                data?.total == null
                  ? cachedTotalPages
                  : Math.ceil(data.total / singleTableState.itemsPerPage),
            },
            onItemsPerPageChange: handleSingleTableItemsPerPageChange,
            onPageChange: handleSingleTablePageChange,
            pageSizeOptions: [10, 25, 50, 100],
            showPageSizeSelector: true,
          }}
          setSortDescriptor={handleSingleTableSortChange}
          sortDescriptor={singleTableState.sortDescriptor}
          useAdvancedSorting={true}
        />
      </div>
    );
  }

  // Render multi-section view (for "all" and "open" filters)
  // Map sections to their data
  const sectionDataMap = {
    "awaiting-reply": awaitingReplyData,
    "being-revised": beingRevisedData,
    "in-progress": inProgressData,
    others: othersData,
    "ready-for-review": readyForReviewData,
  };

  // Map sections to their counts
  const sectionCountsMap: Record<SectionType, number | undefined> = {
    "awaiting-reply": countsData?.counts.assignedToAny.awaitingReply,
    "being-revised": countsData?.counts.assignedToAny.beingRevised,
    "in-progress": countsData?.counts.assignedToAny.inProgress,
    others: countsData?.counts.assignedToAny.others,
    "ready-for-review": countsData?.counts.assignedToAny.readyForReview,
  };

  return (
    <div className="space-y-6">
      {SECTIONS.map((section) => {
        const sectionState = sectionStates[section.id];
        const handlers = createSectionHandlers(section.id);
        const { data, isLoading } = sectionDataMap[section.id];

        const effectiveTotal = searchValue
          ? (data?.total ?? null)
          : (sectionCountsMap[section.id] ?? null);
        const effectiveCount = searchValue
          ? data?.total
          : sectionCountsMap[section.id];

        return (
          <div key={section.id}>
            <LazyConfigurableTable
              config={{
                ...getSectionConfig(section.id),
                id: `assigned-to-any-${section.id}`,
                titleNode: <SectionTitleBadge sectionId={section.id} />,
              }}
              count={effectiveCount}
              data={data?.requests || []}
              isCountLoading={isCountsLoading}
              isLoading={isLoading}
              paginationProps={{
                metadata: {
                  currentPage: sectionState.currentPage,
                  itemsPerPage: sectionState.itemsPerPage,
                  totalItems: effectiveTotal,
                  totalPages: effectiveTotal
                    ? Math.ceil(effectiveTotal / sectionState.itemsPerPage)
                    : null,
                },
                onItemsPerPageChange: handlers.onItemsPerPageChange,
                onPageChange: handlers.onPageChange,
                pageSizeOptions: [10, 25, 50, 100],
                showPageSizeSelector: true,
              }}
              setSortDescriptor={handlers.onSortChange}
              sortDescriptor={sectionState.sortDescriptor}
              useAdvancedSorting={true}
            />
          </div>
        );
      })}
    </div>
  );
}

export const AssignedToAnyTab = memo(AssignedToAnyTabComponent);
